import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, Notification } from '@/hooks/useNotifications';
import { formatDistanceToNow, format, startOfTomorrow, addDays } from 'date-fns';
import { X, Clock, MoreHorizontal, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

/**
 * Calculate snooze time in user's local timezone, return as UTC ISO string.
 * Keeps timezone logic in frontend where browser knows user's timezone.
 */
function getSnoozeUntil(duration: 'tomorrow' | '3_days' | '1_week'): string {
  let target: Date;

  if (duration === 'tomorrow') {
    // Tomorrow at 8 AM local time
    target = startOfTomorrow();
    target.setHours(8, 0, 0, 0);
  } else if (duration === '3_days') {
    target = addDays(new Date(), 3);
  } else {
    target = addDays(new Date(), 7);
  }

  return target.toISOString(); // Converts to UTC
}

interface NotificationListProps {
  notifications: Notification[];
  showSnoozeActions?: boolean;  // Show snooze controls (for dropdown/active tab)
  showUnsnoozeAction?: boolean; // Show unsnooze (for snoozed tab)
}

const priorityColors: Record<string, string> = {
  critical: 'border-l-red-600 bg-red-50',
  urgent: 'border-l-orange-500 bg-orange-50',
  high: 'border-l-yellow-500 bg-yellow-50',
  medium: 'border-l-blue-500',
  low: 'border-l-gray-300',
};

// Context-aware dismiss messages
const dismissMessages: Record<string, string> = {
  permit: "You won't see this specific notification again, but you may receive future reminders if the permit deadline approaches.",
  invoice: "This notification will be dismissed. You may still receive escalation reminders if the invoice remains unpaid.",
  billable_item: "This notification will be dismissed. It may return if the available-to-bill amount increases.",
  default: "This notification will be dismissed and moved to your history.",
};

function getEntityPath(notification: Notification): string {
  const { entity_type, entity_id, project_id } = notification;

  // Handle aggregate notifications (e.g., threshold alerts)
  if (entity_id === 'aggregate') {
    switch (entity_type) {
      case 'invoice':
        return '/reports?tab=outstanding-invoices';
      case 'billable_item':
        return '/reports?tab=available-to-bill';
      default:
        return '/reports';
    }
  }

  switch (entity_type) {
    case 'permit':
      return project_id ? `/projects/${project_id}/permits/${entity_id}` : '#';
    case 'invoice':
      return `/invoices/${entity_id}`;
    case 'project':
      return `/projects/${entity_id}`;
    case 'scope':
      return project_id ? `/projects/${project_id}?scope=${entity_id}` : '#';
    case 'billable_item':
      return project_id ? `/projects/${project_id}/billable-items/${entity_id}` : '#';
    default:
      return '#';
  }
}

export function NotificationList({
  notifications,
  showSnoozeActions = true,
  showUnsnoozeAction = false,
}: NotificationListProps) {
  const navigate = useNavigate();
  const { markAsRead, dismiss, snooze, unsnooze, confirmAddon, isConfirmingAddon } = useNotifications();
  const [dismissConfirm, setDismissConfirm] = useState<Notification | null>(null);
  const [confirmingAddonId, setConfirmingAddonId] = useState<string | null>(null);

  const handleClick = (notification: Notification) => {
    // Add-on prompt notifications use inline action buttons — don't navigate on click
    if (notification.type === 'ca_addon_review_prompt') return;
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    const path = getEntityPath(notification);
    if (path !== '#') {
      navigate(path);
    }
  };

  const handleConfirmAddon = async (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation();
    if (!notification.project_id) return;
    setConfirmingAddonId(notification.id);
    try {
      await confirmAddon(notification.project_id);
      dismiss(notification.id);
    } catch {
      // Error is swallowed — notification stays visible so PM can retry
    } finally {
      setConfirmingAddonId(null);
    }
  };

  const handleDeclineAddon = (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation();
    dismiss(notification.id);
  };

  const handleDismissClick = (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation();
    // Show confirmation for high-priority items
    if (['permit', 'invoice'].includes(notification.entity_type) ||
        ['critical', 'urgent', 'high'].includes(notification.priority)) {
      setDismissConfirm(notification);
    } else {
      dismiss(notification.id);
    }
  };

  const handleConfirmDismiss = () => {
    if (dismissConfirm) {
      dismiss(dismissConfirm.id);
      setDismissConfirm(null);
    }
  };

  const handleSnooze = (e: React.MouseEvent, notificationId: string, duration: 'tomorrow' | '3_days' | '1_week') => {
    e.stopPropagation();
    const until = getSnoozeUntil(duration);
    snooze({ notificationId, until });
  };

  const handleUnsnooze = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    unsnooze(notificationId);
  };

  return (
    <>
      <div className="divide-y">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            onClick={() => handleClick(notification)}
            data-testid="notification-item"
            data-notification-id={notification.id}
            data-unread={!notification.is_read && !notification.is_snoozed ? 'true' : 'false'}
            data-dismissed={notification.is_dismissed ? 'true' : 'false'}
            data-snoozed={notification.is_snoozed ? 'true' : 'false'}
            data-priority={notification.priority}
            className={cn(
              'px-3 py-3 cursor-pointer hover:bg-muted/50 border-l-4 relative group',
              priorityColors[notification.priority] || 'border-l-gray-300',
              !notification.is_read && !notification.is_snoozed && 'bg-muted/30',
              notification.is_dismissed && 'opacity-60'
            )}
          >
            <div className="flex justify-between items-start gap-2">
              <div className="flex-1 min-w-0">
                <p className={cn(
                  'text-sm truncate',
                  !notification.is_read && !notification.is_snoozed && 'font-semibold'
                )}>
                  {notification.title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                  {notification.message}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                  </p>
                  {notification.is_snoozed && notification.snoozed_until && (
                    <span className="text-xs text-amber-600 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Until {format(new Date(notification.snoozed_until), 'MMM d')}
                    </span>
                  )}
                  {notification.is_dismissed && (
                    <span className="text-xs text-muted-foreground">(dismissed)</span>
                  )}
                </div>

                {/* CA add-on review prompt: inline confirm/decline actions */}
                {notification.type === 'ca_addon_review_prompt' && !notification.is_dismissed && notification.project_id && (
                  <div className="flex gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="default"
                      disabled={confirmingAddonId === notification.id || isConfirmingAddon}
                      onClick={(e) => handleConfirmAddon(e, notification)}
                    >
                      {confirmingAddonId === notification.id ? 'Confirming…' : 'Confirm Add-On ($500)'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={confirmingAddonId === notification.id}
                      onClick={(e) => handleDeclineAddon(e, notification)}
                    >
                      No Thanks
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Actions dropdown */}
                {(showSnoozeActions || showUnsnoozeAction) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      {showSnoozeActions && !notification.is_snoozed && (
                        <>
                          <DropdownMenuItem onClick={(e) => handleSnooze(e as unknown as React.MouseEvent, notification.id, 'tomorrow')}>
                            <Clock className="h-4 w-4 mr-2" />
                            Snooze until tomorrow
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => handleSnooze(e as unknown as React.MouseEvent, notification.id, '3_days')}>
                            <Clock className="h-4 w-4 mr-2" />
                            Snooze for 3 days
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => handleSnooze(e as unknown as React.MouseEvent, notification.id, '1_week')}>
                            <Clock className="h-4 w-4 mr-2" />
                            Snooze for 1 week
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      {showUnsnoozeAction && notification.is_snoozed && (
                        <>
                          <DropdownMenuItem onClick={(e) => handleUnsnooze(e as unknown as React.MouseEvent, notification.id)}>
                            <Bell className="h-4 w-4 mr-2" />
                            Unsnooze now
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      {!notification.is_dismissed && (
                        <DropdownMenuItem
                          onClick={(e) => handleDismissClick(e as unknown as React.MouseEvent, notification)}
                          className="text-destructive"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Dismiss
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Quick dismiss button (for low-priority in dropdown) */}
                {!showSnoozeActions && !showUnsnoozeAction && !notification.is_dismissed && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => handleDismissClick(e, notification)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {!notification.is_read && !notification.is_snoozed && !notification.is_dismissed && (
              <div className="absolute top-3 right-10 w-2 h-2 bg-primary rounded-full" />
            )}
          </div>
        ))}
      </div>

      {/* Dismiss confirmation dialog */}
      <AlertDialog open={!!dismissConfirm} onOpenChange={() => setDismissConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dismiss this notification?</AlertDialogTitle>
            <AlertDialogDescription>
              {dismissConfirm && (dismissMessages[dismissConfirm.entity_type] || dismissMessages.default)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDismiss}>
              Dismiss
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
